<template>
  <v-menu
    :close-on-content-click="false"
    offset-y
    v-model="menu"
    content-class="rounded-lg rounded-tl-0"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-bind="attrs"
        v-on="on"
        :value="formattedLabel"
        readonly
        prepend-inner-icon="mdi-calendar"
        :label="label || 'Datas'"
        @click="menu = true"
        :block="block"
        style="max-width: 400px"
        :style="contentStyle"
        :color="color"
        :outlined="outlined"
        :small="small"
        :disabled="disabled"
        :loading="loading"
        :class="contentClass"
        :dense="dense"
        depressed
        hide-details
      >
      </v-text-field>
    </template>
    <v-card class="pa-3 pt-4 rounded-lg rounded-tl-0" style="width: 400px">
      <div class="d-flex gap-2">
        <v-text-field
          :value="dateRange.start"
          @input="setStart"
          label="Data de início"
          type="date"
          outlined
          hide-details
          clearable
          dense
        ></v-text-field>
        <v-text-field
          :value="dateRange.end"
          @input="setEnd"
          label="Data de fim"
          type="date"
          outlined
          hide-details
          clearable
          dense
        ></v-text-field>
      </div>
      <small class="d-block mt-2"> Filtros rápidos </small>
      <v-chip-group
        small
        :value="JSON.stringify(dateRange)"
        column
        active-class="primary--text"
      >
        <v-chip
          v-for="helper in helpersOptions"
          :key="helper.text"
          small
          filter
          :value="JSON.stringify(helper.value)"
          @change="setDateRange(helper.value)"
        >
          {{ helper.text }}
        </v-chip>
      </v-chip-group>

      <div class="d-flex justify-space-between gap-2 mt-4">
        <v-btn small color="primary" @click="cancel" text> Cancelar </v-btn>
        <v-btn small color="primary" @click="save" depressed> Salvar </v-btn>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
import dayjs from "dayjs";

export default {
  name: "DateRangePicker",
  props: {
    value: {
      type: Object,
      default: () => ({
        start: null,
        end: null,
      }),
    },
    contentStyle: {
      type: Object,
      default: () => ({}),
    },
    contentClass: {
      type: String,
      default: "",
    },
    block: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "primary",
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    small: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: "Datas",
    },
  },
  data() {
    return {
      menu: false,
      dateRange: {
        start: null,
        end: null,
      },
      helpersOptions: [
        {
          text: "Hoje",
          value: {
            start: dayjs().startOf("day").format("YYYY-MM-DD"),
            end: dayjs().endOf("day").format("YYYY-MM-DD"),
          },
        },
        {
          text: "Ontem",
          value: {
            start: dayjs()
              .subtract(1, "day")
              .startOf("day")
              .format("YYYY-MM-DD"),
            end: dayjs().subtract(1, "day").endOf("day").format("YYYY-MM-DD"),
          },
        },
        {
          text: "Últimos 7 dias",
          value: {
            start: dayjs()
              .subtract(7, "day")
              .startOf("day")
              .format("YYYY-MM-DD"),
            end: dayjs().endOf("day").format("YYYY-MM-DD"),
          },
        },
        {
          text: "Esse mês",
          value: {
            start: dayjs().startOf("month").format("YYYY-MM-DD"),
            end: dayjs().endOf("month").format("YYYY-MM-DD"),
          },
        },
        {
          text: "Últimos 30 dias",
          value: {
            start: dayjs()
              .subtract(30, "day")
              .startOf("day")
              .format("YYYY-MM-DD"),
            end: dayjs().endOf("day").format("YYYY-MM-DD"),
          },
        },
        {
          text: "Últimos 90 dias",
          value: {
            start: dayjs()
              .subtract(90, "day")
              .startOf("day")
              .format("YYYY-MM-DD"),
            end: dayjs().endOf("day").format("YYYY-MM-DD"),
          },
        },
      ],
    };
  },
  watch: {
    value: {
      handler(value) {
        this.dateRange = Object.assign({ start: null, end: null }, value);
      },
    },
    menu: {
      handler(value) {
        if (!value) this.save();
      },
    },
  },
  computed: {
    formattedLabel() {
      if (this.value.start && this.value.end) {
        const helper = this.helpersOptions.find(
          (helper) =>
            helper.value.start === this.value.start &&
            helper.value.end === this.value.end
        );
        if (helper) return helper.text;

        return `${dayjs(this.value.start).format("DD/MM/YYYY")} - ${dayjs(
          this.value.end
        ).format("DD/MM/YYYY")}`;
      }
      if (this.value.start) {
        return `A partir de ${dayjs(this.value.start).format("DD/MM/YYYY")}`;
      }
      if (this.value.end) {
        return `Até ${dayjs(this.value.end).format("DD/MM/YYYY")}`;
      }
      return "Data";
    },
  },
  methods: {
    save() {
      this.$emit("input", this.dateRange);
      this.menu = false;
    },
    setStart(value) {
      if (value === this.dateRange.start) return;
      const dateRange = { ...this.dateRange };
      if (dateRange.end && dayjs(value).isAfter(dayjs(dateRange.end))) {
        dateRange.end = value;
      }
      dateRange.start = value;
      this.dateRange = dateRange;
    },
    setEnd(value) {
      if (value === this.dateRange.end) return;
      const dateRange = { ...this.dateRange };
      if (dateRange.start && dayjs(value).isBefore(dayjs(dateRange.start))) {
        dateRange.start = value;
      }
      dateRange.end = value;
      this.dateRange = dateRange;
    },
    setDateRange(value) {
      this.dateRange = value;
    },
    cancel() {
      this.dateRange = Object.assign({ start: null, end: null }, this.value);
      this.menu = false;
    },
  },
  mounted() {
    this.dateRange = Object.assign({ start: null, end: null }, this.value);
  },
};
</script>
